import { databases, DATABASE_ID, COLLECTION_ID_TOTAL_ENERGIES_PO } from '../config/appwrite';
import { ID, Query } from 'appwrite';

const COLLECTION_SETUP_HINT =
  'Create the TotalEnergies PO collection: in the project folder run `npm run setup:total-energies-po` (set APPWRITE_API_KEY in .env). Then wait until attributes are "available" in Appwrite Console.';

/** Appwrite returns different messages/codes when the collection id does not exist yet. */
function isTotalEnergiesCollectionMissing(error) {
  if (!error) return false;
  if (error.code === 404) return true;
  const msg = String(error.message || '').toLowerCase();
  if (msg.includes('could not be found')) return true;
  if (msg.includes('collection') && msg.includes('not found')) return true;
  if (msg.includes('totalenergiesposgm')) return true;
  return false;
}

const formatRow = (doc) => ({
  id: doc.$id,
  documentId: doc.$id,
  projectName: doc.projectName || '',
  sdNo: doc.sdNo || '',
  poNo: doc.poNo || '',
  poDate: doc.poDate || '',
  poAmount: typeof doc.poAmount === 'number' ? doc.poAmount : Number(doc.poAmount) || 0,
  amountInvoiced: typeof doc.amountInvoiced === 'number' ? doc.amountInvoiced : Number(doc.amountInvoiced) || 0,
  poBalance: typeof doc.poBalance === 'number' ? doc.poBalance : Number(doc.poBalance) || 0
});

export const getAllTotalEnergiesPos = async () => {
  try {
    const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_TOTAL_ENERGIES_PO, [
      Query.orderDesc('$createdAt'),
      Query.limit(500)
    ]);
    return (response.documents || []).map(formatRow);
  } catch (error) {
    console.error('Error fetching TotalEnergies PO rows:', error);
    if (isTotalEnergiesCollectionMissing(error)) {
      return [];
    }
    throw error;
  }
};

/**
 * @param {object} data projectName, sdNo, poNo, poDate, poAmount, amountInvoiced, poBalance (numbers)
 */
export const createTotalEnergiesPo = async (data) => {
  const poAmount = Number(data.poAmount) || 0;
  const amountInvoiced = Number(data.amountInvoiced) || 0;
  const poBalance =
    data.poBalance != null && Number.isFinite(Number(data.poBalance))
      ? Number(data.poBalance)
      : Math.max(0, poAmount - amountInvoiced);

  const documentData = {
    projectName: String(data.projectName || '').trim(),
    sdNo: String(data.sdNo || '').trim(),
    poNo: String(data.poNo || '').trim(),
    poDate: String(data.poDate || '').trim(),
    poAmount,
    amountInvoiced,
    poBalance
  };

  try {
    const response = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_TOTAL_ENERGIES_PO,
      ID.unique(),
      documentData
    );
    return formatRow(response);
  } catch (error) {
    if (isTotalEnergiesCollectionMissing(error)) {
      const err = new Error(`${COLLECTION_SETUP_HINT} Original: ${error.message || error}`);
      err.code = 'TOTAL_ENERGIES_PO_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};

export const updateTotalEnergiesPo = async (documentId, data) => {
  const patch = {};
  if (data.projectName !== undefined) patch.projectName = String(data.projectName).trim();
  if (data.sdNo !== undefined) patch.sdNo = String(data.sdNo).trim();
  if (data.poNo !== undefined) patch.poNo = String(data.poNo).trim();
  if (data.poDate !== undefined) patch.poDate = String(data.poDate).trim();
  if (data.poAmount !== undefined) patch.poAmount = Number(data.poAmount) || 0;
  if (data.amountInvoiced !== undefined) patch.amountInvoiced = Number(data.amountInvoiced) || 0;
  if (data.poBalance !== undefined) {
    patch.poBalance = Number(data.poBalance) || 0;
  } else if (data.poAmount !== undefined && data.amountInvoiced !== undefined) {
    patch.poBalance = Math.max(0, Number(data.poAmount) - Number(data.amountInvoiced));
  }

  try {
    const response = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_TOTAL_ENERGIES_PO,
      documentId,
      patch
    );
    return formatRow(response);
  } catch (error) {
    if (isTotalEnergiesCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_PO_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};

export const deleteTotalEnergiesPo = async (documentId) => {
  try {
    await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_TOTAL_ENERGIES_PO, documentId);
  } catch (error) {
    if (isTotalEnergiesCollectionMissing(error)) {
      const err = new Error(COLLECTION_SETUP_HINT);
      err.code = 'TOTAL_ENERGIES_PO_COLLECTION_MISSING';
      throw err;
    }
    throw error;
  }
};
